import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import type { TikTokPostInfoState } from '@/hooks/use-tiktok-post-info';

const MUSIC_USAGE_URL = 'https://www.tiktok.com/legal/page/global/music-usage-confirmation/en';
const BRANDED_CONTENT_URL = 'https://www.tiktok.com/legal/page/global/bc-policy/en';

const PRIVACY_LABEL_KEYS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'publishing.tiktok.privacy.everyone',
  MUTUAL_FOLLOW_FRIENDS: 'publishing.tiktok.privacy.friends',
  FOLLOWER_OF_CREATOR: 'publishing.tiktok.privacy.followers',
  SELF_ONLY: 'publishing.tiktok.privacy.onlyMe',
};

/** Creator-info loading / error / "posting as" lines. */
export const TikTokCreatorStatus: React.FC<{
  postInfo: TikTokPostInfoState;
  fallbackName?: string | null;
  onReconnect: () => void;
}> = ({ postInfo, fallbackName, onReconnect }) => {
  const { t } = useTranslation();
  const { creator, loadingCreator, creatorError } = postInfo;
  return (
    <>
      {loadingCreator && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t('publishing.tiktok.loadingCreator')}
        </div>
      )}

      {creatorError && !loadingCreator && (
        <div className="space-y-3" data-testid="tiktok-creator-error">
          <p className="text-sm text-muted-foreground">{t('publishing.tiktok.creatorError')}</p>
          <Button type="button" onClick={onReconnect}>{t('publishing.reconnectAction')}</Button>
        </div>
      )}

      {creator && (
        <p className="text-sm" data-testid="tiktok-creator-nickname">
          {t('publishing.tiktok.postingAs', {
            name: creator.creator_nickname || fallbackName,
          })}
        </p>
      )}
    </>
  );
};

/**
 * Privacy select, interaction toggles, commercial-content disclosure, AI
 * label and the music-usage declaration. `photoPost` hides the video-only
 * Duet/Stitch toggles and AI label (TikTok photo posts support neither).
 * `children` render between the AI label and the declaration.
 */
export const TikTokPostInfoFields: React.FC<{
  postInfo: TikTokPostInfoState;
  photoPost?: boolean;
  children?: React.ReactNode;
}> = ({ postInfo, photoPost = false, children }) => {
  const { t } = useTranslation();
  const {
    creator,
    privacyLevel,
    setPrivacyLevel,
    visiblePrivacyOptions,
    brandedBlocksPrivate,
    allowComment,
    setAllowComment,
    allowDuet,
    setAllowDuet,
    allowStitch,
    setAllowStitch,
    commercial,
    setCommercial,
    yourBrand,
    setYourBrand,
    brandedContent,
    setBrandedContent,
    isAigc,
    setIsAigc,
    musicConfirmed,
    setMusicConfirmed,
  } = postInfo;

  const declarationKey = commercial && brandedContent
    ? 'publishing.tiktok.declarationBranded'
    : 'publishing.tiktok.declarationMusic';

  return (
    <>
      <div className="space-y-2">
        <Label>{t('publishing.tiktok.privacyLabel')}</Label>
        <Select
          value={privacyLevel || undefined}
          onValueChange={setPrivacyLevel}
        >
          <SelectTrigger data-testid="tiktok-privacy-select">
            <SelectValue placeholder={t('publishing.tiktok.privacyPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {visiblePrivacyOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {t(PRIVACY_LABEL_KEYS[option] || option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {brandedBlocksPrivate && (
          <p className="text-xs text-muted-foreground">{t('publishing.tiktok.brandedNotPrivate')}</p>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">{t('publishing.tiktok.interactionsLabel')}</p>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={allowComment}
            disabled={!!creator?.comment_disabled}
            onCheckedChange={(checked) => setAllowComment(checked === true)}
            data-testid="tiktok-allow-comment"
          />
          {t('publishing.tiktok.allowComment')}
        </label>
        {!photoPost && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={allowDuet}
                disabled={!!creator?.duet_disabled}
                onCheckedChange={(checked) => setAllowDuet(checked === true)}
                data-testid="tiktok-allow-duet"
              />
              {t('publishing.tiktok.allowDuet')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={allowStitch}
                disabled={!!creator?.stitch_disabled}
                onCheckedChange={(checked) => setAllowStitch(checked === true)}
                data-testid="tiktok-allow-stitch"
              />
              {t('publishing.tiktok.allowStitch')}
            </label>
          </>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="tiktok-commercial">{t('publishing.tiktok.commercialLabel')}</Label>
          <Switch
            id="tiktok-commercial"
            checked={commercial}
            onCheckedChange={(checked) => {
              setCommercial(checked);
              if (!checked) {
                setYourBrand(false);
                setBrandedContent(false);
              }
            }}
            data-testid="tiktok-commercial"
          />
        </div>
        {commercial && (
          <div className="space-y-2 rounded-md border p-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={yourBrand}
                onCheckedChange={(checked) => setYourBrand(checked === true)}
                data-testid="tiktok-your-brand"
              />
              {t('publishing.tiktok.yourBrand')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={brandedContent}
                onCheckedChange={(checked) => setBrandedContent(checked === true)}
                data-testid="tiktok-branded-content"
              />
              {t('publishing.tiktok.brandedContent')}
            </label>
            {yourBrand && !brandedContent && (
              <p className="text-xs text-muted-foreground">{t('publishing.tiktok.yourBrandHint')}</p>
            )}
            {brandedContent && (
              <p className="text-xs text-muted-foreground">{t('publishing.tiktok.brandedHint')}</p>
            )}
            {!yourBrand && !brandedContent && (
              <p className="text-xs text-muted-foreground">{t('publishing.tiktok.commercialRequired')}</p>
            )}
          </div>
        )}
      </div>

      {!photoPost && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={isAigc}
            onCheckedChange={(checked) => setIsAigc(checked === true)}
            data-testid="tiktok-aigc"
          />
          {t('publishing.tiktok.aiGenerated')}
        </label>
      )}

      {children}

      <label className="flex items-start gap-2 text-sm">
        <Checkbox
          checked={musicConfirmed}
          onCheckedChange={(checked) => setMusicConfirmed(checked === true)}
          data-testid="tiktok-music-confirm"
          className="mt-0.5"
        />
        <span>
          {t(declarationKey)}{' '}
          <a href={MUSIC_USAGE_URL} target="_blank" rel="noreferrer" className="underline">
            {t('publishing.tiktok.musicPolicy')}
          </a>
          {commercial && brandedContent && (
            <>
              {' '}
              <a href={BRANDED_CONTENT_URL} target="_blank" rel="noreferrer" className="underline">
                {t('publishing.tiktok.brandedPolicy')}
              </a>
            </>
          )}
        </span>
      </label>
    </>
  );
};

export default TikTokPostInfoFields;
